import { ChevronLeft } from '@mui/icons-material';
import CampaignIcon from '@mui/icons-material/Campaign';
import { Alert, Chip, Grid, Snackbar, Typography } from '@mui/material';
import { createStyles, WithStyles, withStyles } from '@mui/styles';
import { getInvoiceEncrypted } from 'lib/api/paymentLink';
import CustomButton from 'lib/designSystem/Button';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import { formatDate, formatMoney } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import NotifyPayment from '../NotifyPayment';
import WireInformation from '../WireInformation';

const styles = createStyles({
  layoutWrapper: {
    display: 'flex',
    height: '100vh', // Covers the entire viewport height
  },
  content: {
    width: '100%',
    flexGrow: 1, // Allows it to grow and consume available space
    display: 'flex', // Added to facilitate flex-based layout
    flexDirection: 'column', // Stack children vertically
  },
  layout: {
    flexGrow: 1, // Allows it to grow and consume available space
    overflowX: 'auto', // Enables vertical scrolling
    padding: '20px',
    whiteSpace: 'nowrap',
    backgroundColor: '#F8FAFC',
    boxSizing: 'border-box',
    height: '100vh',
  },
  side: {
    flexGrow: 1, // Allows it to grow and consume available space
    overflowX: 'auto', // Enables vertical scrolling
    padding: '20px',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    backgroundColor: '#FFFFFF',
  },
  loading: {
    opacity: 0.6,
    pointerEvents: 'none',
  },
});

interface LayoutProps extends WithStyles<typeof styles> {}

const InvoiceInformation: React.FC<LayoutProps> = ({ classes }) => {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const { encryptedCompanyId, encryptedBusinessIdentifier, invoiceId } =
    useParams<{
      encryptedCompanyId: string;
      encryptedBusinessIdentifier: string;
      invoiceId: string;
    }>();

  const { data, isLoading } = getInvoiceEncrypted(
    encryptedCompanyId,
    encryptedBusinessIdentifier,
    invoiceId,
  );
  if (!data) return null;
  const company = data?.company ?? {};
  const invoice = data?.invoice ?? {};
  const business = data?.business ?? {};
  const invoiceType = data?.type ?? '';
  const payments = data?.payments ?? [];

  const invoiceLeftAmount =
    invoice.amount -
    payments.reduce(
      (acc: number, payment: { amount: number }) => acc + payment.amount,
      0,
    );

  return (
    <div>
      <NotifyPayment
        invoice={invoice}
        open={openModal}
        setOpen={setOpenModal}
      />
      <Snackbar
        onClose={() => setSnackbar({ message: '', open: false })}
        open={snackbar.open}
      >
        <Alert
          onClose={() => setSnackbar({ message: '', open: false })}
          severity="success"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <div className={classes.layoutWrapper}>
        <div className={classes.content}>
          {!isLoading && (
            <Grid container>
              <Grid item xs={3}>
                <div className={classes.side}>
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    {' '}
                    <img
                      width={175}
                      src={`https://pruff-public.s3.amazonaws.com/company-logos/${company.id}.png`}
                    />
                  </div>
                  <WireInformation
                    encryptedBusinessIdentifier={
                      encryptedBusinessIdentifier || ''
                    }
                    encryptedCompanyId={encryptedCompanyId || ''}
                  />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <CustomButton
                    startIcon={<CampaignIcon />}
                    color="primary"
                    variant="contained"
                    style={{ margin: '20px 20px 20px 20px' }}
                    onClick={() => setOpenModal(true)}
                  >
                    Informar pago
                  </CustomButton>
                </div>
              </Grid>
              <Grid item xs={9}>
                <div className={classes.layout}>
                  <HeaderWithActions
                    type="pageHeader"
                    title={`Factura #${invoice.folio}`}
                    variant="h2"
                    buttons={[
                      {
                        action: () => {
                          navigate(
                            `../payment/${encryptedCompanyId}/${encryptedBusinessIdentifier}`,
                          );
                        },
                        color: 'secondary',
                        name: 'Documentos',
                        startIcon: <ChevronLeft />,
                      },
                    ]}
                  />
                  <HeaderWithActions
                    type="subHeader"
                    title={`A continuación encontrarás información relevante del documento.`}
                    variant="body1"
                  />
                  <div
                    style={{
                      backgroundColor: '#FFFFFF',
                      borderRadius: '7px',
                      boxShadow: 'rgba(50, 50, 93, 0.25) 0px 13px 27px -5px',
                      padding: '23px',
                    }}
                  >
                    <center>
                      <Typography
                        style={{ paddingBottom: '10px' }}
                        variant="h3"
                      >
                        {formatMoney(invoice.amount)}
                      </Typography>
                      {invoice.isPaid && (
                        <Chip label="Pagado" color="success" />
                      )}
                      {!invoice.isPaid && (
                        <Chip
                          label={`Por pagar: ${formatMoney(invoiceLeftAmount)}`}
                          color="info"
                        />
                      )}
                      <Grid container>
                        {' '}
                        <Grid item xs={6}>
                          {' '}
                          <Typography variant="h5">Receptor</Typography>
                        </Grid>{' '}
                        <Grid item xs={6}>
                          {' '}
                          <Typography variant="h6">{business.name}</Typography>
                        </Grid>{' '}
                      </Grid>
                      <Grid container>
                        {' '}
                        <Grid item xs={6}>
                          <Typography variant="h5">
                            Emisor {invoiceType === 'cession' && 'Original'}
                          </Typography>
                        </Grid>{' '}
                        <Grid item xs={6}>
                          {' '}
                          <Typography variant="h5">
                            {invoice.business?.name}
                          </Typography>
                        </Grid>{' '}
                      </Grid>
                      {invoiceType === 'cession' && (
                        <Grid container>
                          {' '}
                          <Grid item xs={6}>
                            <Typography variant="h5">Cesionario</Typography>
                          </Grid>{' '}
                          <Grid item xs={6}>
                            {' '}
                            <Typography variant="h5">{company.name}</Typography>
                          </Grid>{' '}
                        </Grid>
                      )}
                      <Grid container>
                        {' '}
                        <Grid item xs={6}>
                          {' '}
                          <Typography variant="h5">Fecha emisión</Typography>
                        </Grid>{' '}
                        <Grid item xs={6}>
                          <Typography variant="h5">
                            {formatDate(new Date(invoice.issuedDate))}
                          </Typography>
                        </Grid>{' '}
                      </Grid>
                      <Grid container></Grid>
                    </center>
                  </div>
                </div>
              </Grid>
            </Grid>
          )}
        </div>
      </div>
    </div>
  );
};

export default withStyles(styles)(InvoiceInformation);
